import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';

interface InboxThread {
  id: string;
  contactId: string | null;
  companyId: string | null;
  campaignId: string | null;
  journeyId: string | null;
  ownerUserId: string | null;
  subject: string | null;
  status: string;
  lastMessageAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface InboxMessage {
  id: string;
  direction: 'inbound' | 'outbound';
  fromEmail: string;
  toEmails: string[];
  subject: string | null;
  textBody: string | null;
  classification: string;
  receivedAt: string;
}

const threadsQuery = {
  queryKey: ['inbox', 'threads'],
  queryFn: () => api.get<{ items: InboxThread[] }>('/api/inbox/threads'),
};

export function InboxPage() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const threads = useQuery(threadsQuery);
  const selected = selectedId ?? threads.data?.items[0]?.id ?? null;
  const detail = useQuery({
    queryKey: ['inbox', 'thread', selected],
    queryFn: () =>
      api.get<{ thread: InboxThread | null; messages: InboxMessage[] }>(
        `/api/inbox/threads/${selected}`,
      ),
    enabled: selected !== null,
  });

  const items = threads.data?.items ?? [];
  return (
    <div className="stack" style={{ gap: 'var(--space-6)' }}>
      <header className="page-header">
        <div>
          <h1 className="text-2xl">Posteingang</h1>
          <p className="muted text-sm">
            Antworten, Zustellfehler und automatische Antworten mit klarer Klassifikation.
          </p>
        </div>
      </header>

      <div className="inbox-layout">
        <section className="card" aria-label="Threads">
          <div className="card__body" style={{ padding: 0 }}>
            {threads.isLoading && (
              <p className="muted" style={{ padding: 'var(--space-4)' }}>
                Wird geladen ...
              </p>
            )}
            {!threads.isLoading && items.length === 0 && (
              <p className="muted" style={{ padding: 'var(--space-4)' }}>
                Keine Nachrichten vorhanden.
              </p>
            )}
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {items.map((thread) => (
                <li key={thread.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(thread.id)}
                    aria-current={selected === thread.id ? 'true' : undefined}
                    className="inbox-thread-item"
                  >
                    <strong>{thread.subject || '(Ohne Betreff)'}</strong>
                    <span className="muted text-sm" style={{ display: 'block', marginTop: 4 }}>
                      {thread.lastMessageAt
                        ? new Date(thread.lastMessageAt).toLocaleString('de-CH')
                        : 'Noch keine Nachricht'}
                    </span>
                    <span className="text-sm" style={{ display: 'inline-block', marginTop: 6 }}>
                      {thread.status === 'open' ? 'Offen' : thread.status}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="card" aria-label="Thread-Details">
          <div className="card__body stack" style={{ gap: 'var(--space-4)' }}>
            {!selected && <p className="muted">Thread auswählen.</p>}
            {detail.isLoading && <p className="muted">Thread wird geladen ...</p>}
            {detail.data?.thread && (
              <>
                <div>
                  <h2 style={{ fontSize: 'var(--text-lg)' }}>
                    {detail.data.thread.subject || '(Ohne Betreff)'}
                  </h2>
                  <p className="muted text-sm">
                    Kontakt: {detail.data.thread.contactId ?? 'nicht zugeordnet'} · Status:{' '}
                    {detail.data.thread.status}
                  </p>
                </div>
                <ol
                  style={{
                    listStyle: 'none',
                    margin: 0,
                    padding: 0,
                    display: 'grid',
                    gap: 'var(--space-3)',
                  }}
                >
                  {detail.data.messages.map((message) => (
                    <li
                      key={message.id}
                      className="inbox-message-item"
                      data-direction={message.direction}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                        <strong>
                          {message.direction === 'inbound'
                            ? message.fromEmail
                            : `An ${message.toEmails.join(', ')}`}
                        </strong>
                        <span className="muted text-sm">
                          {new Date(message.receivedAt).toLocaleString('de-CH')}
                        </span>
                      </div>
                      <p className="muted text-sm" style={{ margin: '4px 0' }}>
                        Klassifikation: {message.classification}
                      </p>
                      <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                        {message.textBody || '(Kein Textinhalt)'}
                      </p>
                    </li>
                  ))}
                </ol>
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
